import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Service, inject } from '@angular/core';
import { forkJoin, from, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { bufferCount, catchError, concatMap, take, tap } from 'rxjs/operators';

import { HttpBasicRequest, HttpBasicRequestOptions, HttpBasicTextRequestOptions } from '../models/http-basic-request.model';
import { LoaderService } from './loader.service';
import { UtilityFunctions } from '../utilities/functions.models';

export class BasicRequestHandler {
  public loaderId: string;
  public subscription: Subscription;
  public running?: boolean;
  /** Subject della richiesta in volo: va chiuso quando la si annulla, o il chiamante resta appeso. */
  public subject?: Subject<any>;
  constructor(init?: Partial<BasicRequestHandler>) {
    Object.assign(this, init);
  }
}

@Service()
export class BasicHttpClientService {

  private _httpClient = inject(HttpClient);
  private _loaderService = inject(LoaderService);

  /** Richieste IN VOLO con un loaderId. Chi finisce (o viene annullato) esce: vedi releaseRequest. */
  public loaders: BasicRequestHandler[] = [];

  /**
   * Apre una richiesta sul `loaderId` e accende il loader, annullando l'eventuale richiesta in volo
   * sullo stesso id. Senza `loaderId` l'handler NON si registra: uno condiviso farebbe annullare a
   * vicenda richieste scorrelate. Dettagli: docs/features/returns/plans/2026-09-11-picker-richieste-appese.md
   */
  private beginRequest(loaderId?: string): BasicRequestHandler {
    const previous = loaderId ? this.loaders.find(x => x.loaderId === loaderId) : null;
    if (previous) this.abortRequest(previous, false);

    const req = new BasicRequestHandler({ loaderId, running: true });
    if (loaderId) this.loaders.push(req);

    this._loaderService.showLoader(loaderId);
    return req;
  }

  /**
   * Toglie l'handler dalla lista delle richieste in volo. Senza questo `loaders` cresceva a ogni
   * loaderId nuovo e non si svuotava mai (il picker dei resi ne conia uno per accordion aperto).
   */
  private releaseRequest(req: BasicRequestHandler): void {
    const index = this.loaders.indexOf(req);
    if (index !== -1) this.loaders.splice(index, 1);
  }

  /** Richiesta conclusa (dato, errore o stream chiuso): handler liberato e loader spento. */
  private settleRequest(req: BasicRequestHandler): void {
    req.running = false;
    req.subject = null;
    this.releaseRequest(req);
    this._loaderService.dismissLoader(req.loaderId);
  }

  /**
   * Annulla la richiesta in volo COMPLETANDO il Subject: un annullamento non è un guasto, mandarlo
   * in errore riempirebbe Application Insights a ogni ricerca digitata.
   * @param dismissLoader false se sullo stesso id riparte subito un'altra richiesta.
   */
  private abortRequest(req: BasicRequestHandler, dismissLoader: boolean = true): void {
    req.subscription?.unsubscribe();
    req.running = false;

    const pending = req.subject;
    req.subject = null;
    this.releaseRequest(req);

    if (dismissLoader) this._loaderService.dismissLoader(req.loaderId);

    pending?.complete();
  }

  /**
   * Ciclo di vita di UNA richiesta: loader, handler e i TRE esiti possibili dello stream.
   *
   * Il terzo — `complete` SENZA emissione — non e' teorico: basta un interceptor che restituisca
   * `of(null)` (vedi AuthInterceptor.handle401Error) perche' HttpClient, che con `observe: 'body'`
   * filtra tutto cio' che non e' HttpResponse, chiuda la sorgente a vuoto. Finche' mancava questo
   * handler il chiamante non riceveva NULLA — ne' dato, ne' errore, ne' chiusura — e la connessione
   * restava appesa: dalla settima in poi Chrome mette ogni richiesta verso quell'host in Stalled.
   *
   * settleRequest e' idempotente perche' su una risposta normale arrivano sia `next` sia `complete`.
   */
  private track<S, R = S>(
    loaderId: string | undefined,
    source: Observable<S>,
    project?: (data: S) => R
  ): Observable<R> {
    const req = this.beginRequest(loaderId);

    // ReplaySubject e non Subject: ci si sottoscrive DOPO che la sorgente e' partita, quindi una
    // emissione sincrona (interceptor che corto-circuita con `of(...)`, mock, cache) andrebbe
    // persa e il chiamante resterebbe di nuovo senza risposta. Il buffer da 1 lo trattiene.
    const subject = new ReplaySubject<R>(1);
    req.subject = subject;
    req.subscription = source.subscribe({
      next: data => {
        this.settleRequest(req);
        subject.next(project ? project(data) : (data as unknown as R));
      },
      error: error => {
        this.settleRequest(req);
        console.error(error);
        subject.error(error);
      },
      complete: () => {
        this.settleRequest(req);
        subject.complete();
      }
    });
    // take(1) e non first(): una richiesta annullata completa SENZA emettere, e first() in quel
    // caso lancerebbe EmptyError trasformando un annullamento legittimo in un errore.
    return subject.asObservable().pipe(take(1));
  }

  public get<T>(
    url: string,
    options?: HttpBasicRequestOptions,
    loaderId?: string
  ): Observable<T> {
    return this.track<T>(loaderId, this._httpClient.get<T>(url, options || new HttpBasicRequestOptions()));
  }

  public getBlob(
    url: string,
    options?: HttpBasicRequestOptions,
    loaderId?: string
  ): Observable<{ blob: Blob; fileName: string; }> {
    return this.track(loaderId, this._httpClient.get(url, {
      responseType: 'blob',
      headers: options?.headers,
      observe: 'response',
      params: options?.params
    }), data => ({
      blob: data.body,
      fileName: UtilityFunctions.getFilenameFromContentDisposition(data.headers.get('Content-Disposition'))
    }));
  }

  public postBlob(
    url: string,
    body: any,
    options?: HttpBasicRequestOptions,
    loaderId?: string
  ): Observable<{ blob: Blob; fileName: string; }> {
    return this.track(loaderId, this._httpClient.post(url, body, {
      responseType: 'blob',
      headers: options?.headers,
      observe: 'response',
      params: options?.params
    }), data => ({
      blob: data.body,
      fileName: UtilityFunctions.getFilenameFromContentDisposition(data.headers.get('Content-Disposition'))
    }));
  }

  public post<T>(
    url: string,
    body: any,
    options?: HttpBasicRequestOptions,
    loaderId?: string
  ): Observable<any> {
    return this.track<T>(loaderId, this._httpClient.post<T>(url, body, options || new HttpBasicRequestOptions()));
  }

  public postTextResponse(
    url: string,
    body: any,
    options?: HttpBasicTextRequestOptions,
    loaderId?: string
  ): Observable<string> {
    return this.track<string>(loaderId, this._httpClient.post(url, body, options || new HttpBasicTextRequestOptions()));
  }

  public put<T>(
    url: string,
    body: any,
    options?: HttpBasicRequestOptions,
    loaderId?: string
  ): Observable<any> {
    return this.track<T>(loaderId, this._httpClient.put<T>(url, body, options || new HttpBasicRequestOptions()));
  }

  public delete<T>(
    url: string,
    options?: HttpBasicRequestOptions,
    loaderId?: string
  ): Observable<any> {
    return this.track<T>(loaderId, this._httpClient.delete<T>(url, options || new HttpBasicRequestOptions()));
  }

  public forkJoin<T>(requests: HttpBasicRequest[], loaderId?: string, buffCount: number = 100): Observable<T[]> {
    requests = requests.filter(x => !!x?.method);
    // Stessa ragione di `track`: la sottoscrizione del chiamante arriva dopo l'avvio dei sources.
    const sbj = new ReplaySubject<T[]>(1);
    if (requests?.length) {
      const req = this.beginRequest(loaderId);
      req.subject = sbj;

      const sources = [];
      requests.forEach(r => {
        if (r.method == 'get' || r.method == 'delete')
          sources.push(
            (this._httpClient[r.method] as (url: string, options?: unknown) => Observable<unknown>)(r.url, r.options || new HttpBasicRequestOptions()).pipe(
              catchError(error => {
                console.error(error);
                return of(null);
              })
            )
          );
        if (r.method == 'post' || r.method == 'put')
          sources.push(
            (this._httpClient[r.method] as (url: string, body: unknown, options?: unknown) => Observable<unknown>)(r.url, r.body || null, r.options || new HttpBasicRequestOptions()).pipe(
              tap({
                next: data => data,
                error: (error: HttpErrorResponse) => {
                  console.error(error);
                }
              })
            )
          );
      });
      const result = [];
      req.subscription = from(sources).pipe(
        bufferCount(buffCount),
        concatMap(tasks => {
          return forkJoin([...tasks]);
        })).subscribe({
          next: res => {
            result.push(...res);
            if (sources.length === result.length) {
              this.settleRequest(req);
              sbj.next(result);
              sbj.complete();
            }
          },
          error: (error: HttpErrorResponse) => {
            console.error(error);
            this.settleRequest(req);
            sbj.complete();
          },
          // Stream chiuso senza aver raccolto tutte le risposte: senza questo il chiamante
          // resterebbe appeso esattamente come nel caso singolo.
          complete: () => {
            this.settleRequest(req);
            sbj.complete();
          }
        });
    }
    return sbj.asObservable();
  }

  /**
   * Annulla esplicitamente la richiesta in volo su un loaderId. NON agganciarla al ciclo di vita
   * di un componente: la vita di un template non e' quella di una chiamata HTTP.
   */
  public destroyLoader(loaderId: string) {
    const req = this.loaders.find(x => x.loaderId === loaderId);
    if (req) this.abortRequest(req);
  }
}
